import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/shop';
import { formatMoney } from '../brand/brand';
import type { ActivityEntry } from '../api/types';

/**
 * The customer's own history: every purchase, spend, tip and refund, newest first.
 *
 * The API has written these rows on every money movement since the wallet was built
 * and nothing ever showed them. Buy fifty pounds of credit, spend twelve at the
 * counter, and the account page showed the fifty and no sign of the twelve — the
 * balance moved and the history was silent.
 *
 * A SPEND LINKS TO ITS RECEIPT; a purchase does not. A purchase has an order, which
 * lives under Orders, and rendering it as a receipt would be inventing a document
 * that does not exist.
 */
export function ActivityList({ voucherId }: { voucherId?: string } = {}) {
  const [entries, setEntries] = useState<ActivityEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = voucherId === undefined
      ? api.activity({ limit: 100 })
      // ONE VOUCHER'S WHOLE HISTORY: the purchase and every visit. Filtered again
      // here because an API older than the `voucherId` parameter ignores it and
      // answers with the whole wallet, which must not pass as one voucher's rows.
      : api.activity({ voucherId, limit: 200 }).then((r) => r.filter((e) => e.voucherId === voucherId));

    load
      .then((r) => { if (!cancelled) setEntries(r); })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, [voucherId]);

  if (failed) {
    return (
      <p className="notice error" role="alert">
        We could not load {voucherId === undefined ? 'your activity' : 'the history for this voucher'} just now. Please try again.
      </p>
    );
  }

  if (entries === null) {
    return (
      <div role="status">
        <span className="visually-hidden">Loading your activity</span>
        <ul className="acct-list" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i}>
              <div className="acct-row">
                <span className="acct-row-main">
                  <span className="skeleton skeleton-line" style={{ width: '58%' }} />
                  <span className="skeleton skeleton-line" style={{ width: '34%' }} />
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (entries.length === 0 && voucherId !== undefined) {
    return <p className="muted">Nothing yet. Each visit shows up here.</p>;
  }

  if (entries.length === 0) {
    return (
      <div className="acct-empty">
        <h2>Nothing here yet</h2>
        <p className="muted">
          Every purchase and every time you spend at a counter shows up here, with a
          receipt you can pull up later.
        </p>
        <Link to="/" className="btn">Browse offers</Link>
      </div>
    );
  }

  return (
    <ul className="acct-list">
      {entries.map((e) => {
        const row = (
          <>
            <span className="acct-row-main">
              <span className="acct-row-title">{label(e)}</span>
              <span className="acct-row-sub">
                {secondary(e, voucherId !== undefined)} · {formatWhen(e.createdAt)}
              </span>
            </span>

            <span className="acct-row-side">
              {/*
                A ZERO-AMOUNT ROW IS NOT A BUG. A tip is recorded against the voucher
                so it appears in this history, with no balance effect, because it was
                paid separately — so the row has AmountCents 0 and its own
                description carries the figure. Rendering the usual signed amount
                here would print "+$0.00" beside a five-dollar tip.
              */}
              {e.amountCents !== 0 && (
                <>
                  {/*
                    The sign is the information. Money leaving a voucher and money
                    arriving on one look identical without it, and "$12.00" against a
                    spend reads as a charge to the card.

                    aria-label spells it out, because a screen reader announcing
                    "minus twelve dollars" is ambiguous between a spend and the
                    refund of one.
                  */}
                  <span
                    className={e.amountCents < 0 ? 'acct-amount out' : 'acct-amount in'}
                    aria-label={`${e.amountCents < 0 ? 'Spent' : 'Added'} ${formatMoney(Math.abs(e.amountCents))}`}
                  >
                    {e.amountCents < 0 ? '−' : '+'}{formatMoney(Math.abs(e.amountCents))}
                  </span>
                  <span className="acct-row-sub">
                    {formatMoney(e.balanceAfterCents)} left
                  </span>
                </>
              )}
            </span>
          </>
        );

        return (
          <li key={e.id}>
            {e.redemptionId === null ? (
              /* Not a link. A row that looks clickable and does nothing is worse
                 than one that plainly is not. */
              <div className="acct-row">{row}</div>
            ) : (
              <Link to={`/receipts/${e.redemptionId}`} className="acct-row">{row}</Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The customer's words for what happened, not the database's.
 *
 * `type` is a storage vocabulary — purchase, redeem, clawback — and "clawback" on
 * somebody's statement is alarming in a way the underlying event usually is not.
 */
function label(e: ActivityEntry): string {
  const what = e.dealTitle ?? 'Voucher';

  switch (e.type) {
    case 'purchase': return `Bought ${what}`;
    case 'redeem': return `Spent at ${e.businessName}`;
    case 'tip': return 'Tip';
    case 'refund': return `Refund · ${what}`;
    case 'clawback': return 'Balance corrected';
    // What was left when the voucher's end date passed. Plain words: the customer
    // should recognise this as the thing the reminder email warned them about.
    case 'expire': return `Expired unused · ${what}`;
    default: return e.description ?? 'Adjustment';
  }
}

/**
 * The other half of the story, chosen so the row never says the same thing twice.
 *
 * "Spent at The Harbour Kitchen" above "The Harbour Kitchen · 14 Jun" wasted the
 * widest line on the page repeating a name the customer had just read.
 */
function secondary(e: ActivityEntry, oneVoucher: boolean): string {
  // A tip's amount lives ONLY in its description — see the zero-amount note above —
  // so this is where the customer finds out how much they tipped.
  if (e.type === 'tip') {
    return e.description ?? e.businessName;
  }

  // On a single voucher's page the deal title is the page heading, so a spend
  // shows where it happened (the description names the counter) instead.
  if (oneVoucher && e.type === 'redeem') {
    return e.description !== null && e.description !== '' ? e.description : e.businessName;
  }

  return e.type === 'redeem'
    ? (e.dealTitle ?? e.businessName)
    : e.businessName;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
